import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Unlock, Save, Info, Headphones, BookOpen, PenTool, Mic } from 'lucide-react';
import { toast } from 'sonner';

export function AdminAccessManager() {
  const { user } = useAuth();
  const [freeBlocs, setFreeBlocs] = useState({
    comprehension_orale: 1,
    comprehension_ecrite: 1,
    expression_ecrite: 1,
    expression_orale: 1
  });
  
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const accessConfig = await settingsAPI.getCenterAccessConfig(user.id);
      const loadedPacks = await settingsAPI.getCenterPacks(user.id);
      
      if (accessConfig?.free_blocs) {
        setFreeBlocs(accessConfig.free_blocs);
      }
      if (loadedPacks) {
        setPacks(loadedPacks);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    { 
      id: 'comprehension_orale', 
      name: 'Compréhension Orale', 
      icon: Headphones,
      color: 'blue',
      type: 'série'
    },
    { 
      id: 'comprehension_ecrite', 
      name: 'Compréhension Écrite', 
      icon: BookOpen,
      color: 'purple',
      type: 'série'
    },
    { 
      id: 'expression_ecrite', 
      name: 'Expression Écrite', 
      icon: PenTool,
      color: 'green',
      type: 'bloc'
    },
    { 
      id: 'expression_orale', 
      name: 'Expression Orale', 
      icon: Mic,
      color: 'orange',
      type: 'bloc'
    }
  ];

  const handleFreeBlocsChange = (moduleId, value) => {
    const numValue = parseInt(value) || 0;
    setFreeBlocs(prev => ({
      ...prev,
      [moduleId]: numValue
    }));
  };

  const handlePackLimitChange = (packIndex, moduleId, value) => {
    const numValue = value === '' ? '' : (parseInt(value, 10) || 0);
    setPacks(prev => {
      const updated = prev.map((p, i) =>
        i === packIndex
          ? { ...p, blocs_limits: { ...(p.blocs_limits || {}), [moduleId]: numValue } }
          : p
      );
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      // Update free blocs in center access config
      await settingsAPI.updateCenterAccessConfig(user.id, {
        free_blocs: freeBlocs
      });
      
      // Update packs with blocs limits
      for (const pack of packs) {
        const bl = pack.blocs_limits || {};
        const normalized = {};
        for (const k of Object.keys(bl)) {
          normalized[k] = bl[k] === '' ? 0 : parseInt(bl[k], 10) || 0;
        }
        await settingsAPI.updateCenterPack(user.id, pack.id, { blocs_limits: normalized });
      }
      
      toast.success('Configuration des accès sauvegardée !');
    } catch (error) {
      console.error('Error saving access config:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
      green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800'
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Lock className="h-6 w-6 text-primary" />
            Gestion des accès de mon centre
          </h2>
          <p className="text-muted-foreground mt-1">
            Configurez le nombre de blocs/séries accessibles gratuitement et par forfait pour votre centre
          </p>
        </div>
        <button
          onClick={handleSave}
          data-testid="save-access-btn"
          className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg"
        >
          <Save className="h-5 w-5" />
          <span>Sauvegarder</span>
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">Comment ça fonctionne :</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
              <li><strong>Blocs gratuits</strong> : Accessibles sans abonnement pour les utilisateurs de votre centre</li>
              <li><strong>Blocs par forfait</strong> : Accessibles selon le forfait de l'utilisateur</li>
              <li><strong>-1 = Illimité</strong> : Accès à tous les blocs disponibles</li>
              <li>Les blocs au-delà de la limite afficheront un cadenas</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Free Blocs Section */}
      <div className="bg-card rounded-xl border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Unlock className="h-5 w-5 text-green-500" />
          <h3 className="text-lg font-semibold">Blocs/Séries gratuits (sans abonnement)</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {modules.map((module) => {
            const Icon = module.icon;
            return (
              <div
                key={module.id}
                className={`rounded-xl border p-4 ${getColorClasses(module.color)}`}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <Icon className="h-5 w-5" />
                  <span className="font-medium text-sm">{module.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    value={freeBlocs[module.id] || 0}
                    onChange={(e) => handleFreeBlocsChange(module.id, e.target.value)}
                    data-testid={`free-${module.id}`}
                    className="w-20 h-10 px-3 rounded-lg border bg-white dark:bg-gray-800 text-center font-bold text-lg"
                  />
                  <span className="text-sm">{module.type}(s) gratuit(s)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Packs Limits Section */}
      <div className="bg-card rounded-xl border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Lock className="h-5 w-5 text-orange-500" />
          <h3 className="text-lg font-semibold">Limites par forfait</h3>
        </div>

        <div className="space-y-6">
          {packs.map((pack, packIndex) => (
            <div
              key={pack.id}
              className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-5 border"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`px-4 py-2 rounded-lg font-bold text-white ${
                    pack.name?.toLowerCase().includes('gratuit') ? 'bg-gray-500' :
                    pack.name?.toLowerCase().includes('silver') ? 'bg-gray-400' :
                    pack.name?.toLowerCase().includes('gold') ? 'bg-yellow-500' : 'bg-primary'
                  }`}>
                    {pack.name}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {pack.price}$ • {pack.duration}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {modules.map((module) => {
                  const Icon = module.icon;
                  const currentLimit = pack.blocs_limits?.[module.id] ?? pack.blocsLimits?.[module.id] ?? 0;
                  
                  return (
                    <div key={module.id} className="bg-white dark:bg-gray-900 rounded-lg p-3 border">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium truncate">{module.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="-1"
                          value={currentLimit}
                          onChange={(e) => handlePackLimitChange(packIndex, module.id, e.target.value)}
                          data-testid={`pack-${pack.id}-${module.id}`}
                          className="w-16 h-9 px-2 rounded-lg border bg-background text-center font-bold"
                        />
                        <span className="text-xs text-muted-foreground">
                          {currentLimit === -1 ? '∞' : module.type + 's'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview Section */}
      <div className="bg-card rounded-xl border p-6">
        <h3 className="text-lg font-semibold mb-4">Aperçu des accès</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-medium">Module</th>
                <th className="text-center py-3 px-4 font-medium">
                  <span className="text-green-600">Gratuit</span>
                </th>
                {packs.map((pack) => (
                  <th key={pack.id} className="text-center py-3 px-4 font-medium">
                    <span className={
                      pack.name?.toLowerCase().includes('gratuit') ? 'text-gray-500' :
                      pack.name?.toLowerCase().includes('silver') ? 'text-gray-500' :
                      pack.name?.toLowerCase().includes('gold') ? 'text-yellow-600' : ''
                    }>
                      {pack.name}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map((module) => {
                const Icon = module.icon;
                return (
                  <tr key={module.id} className="border-b hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span>{module.name}</span>
                      </div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">
                        {freeBlocs[module.id] || 0}
                      </span>
                    </td>
                    {packs.map((pack) => {
                      const limit = pack.blocs_limits?.[module.id] ?? pack.blocsLimits?.[module.id] ?? 0;
                      return (
                        <td key={pack.id} className="text-center py-3 px-4">
                          <span className={`px-3 py-1 rounded-full font-medium ${
                            limit === -1 
                              ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' 
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                          }`}>
                            {limit === -1 ? '∞ Illimité' : limit}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
